import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Wallet, TrendingUp, AlertCircle, BookOpen, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const inr = (paise) => `₹${(paise / 100).toLocaleString('en-IN')}`;

export const FeeReceivable = () => {
  const [tab, setTab] = useState('courses');
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/registrations`, { withCredentials: true })
      .then(({ data }) => setRegistrations(data))
      .catch(() => setRegistrations([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const kind = tab === 'courses' ? 'course' : 'event';
    return registrations.filter((r) => r.kind === kind);
  }, [tab, registrations]);

  const summary = useMemo(() => {
    const totalExpected = filtered.reduce((s, r) => s + (r.amount || 0), 0);
    const totalReceived = filtered.reduce((s, r) => s + ((r.paid_amount ?? (r.status === 'completed' ? r.amount : 0)) || 0), 0);
    const totalDue = Math.max(totalExpected - totalReceived, 0);
    return { totalExpected, totalReceived, totalDue, count: filtered.length };
  }, [filtered]);

  return (
    <div className="space-y-6" data-testid="fee-receivable">
      <div>
        <h2 className="text-2xl font-heading text-charcoal-800 flex items-center gap-2">
          <Wallet size={22} className="text-saffron-600" /> Fee Receivable
        </h2>
        <p className="text-sm text-charcoal-500 mt-1">Track amounts received, outstanding balances, and per-registration transactions.</p>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-charcoal-100">
        {[
          { id: 'courses', label: 'Courses', icon: BookOpen },
          { id: 'events', label: 'Events', icon: Calendar },
        ].map((s) => {
          const Icon = s.icon;
          const active = tab === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setTab(s.id)}
              data-testid={`fee-receivable-tab-${s.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 -mb-px border-b-2 text-sm font-medium transition-colors ${
                active ? 'border-saffron-600 text-saffron-700' : 'border-transparent text-charcoal-500 hover:text-charcoal-700'
              }`}
            >
              <Icon size={16} /> {s.label}
            </button>
          );
        })}
      </div>

      {/* Summary cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-charcoal-100 p-5">
          <div className="text-xs text-charcoal-500 uppercase tracking-wider mb-2">Total Registrations</div>
          <div className="text-2xl font-heading text-charcoal-800">{summary.count}</div>
        </div>
        <div className="bg-white rounded-xl border border-charcoal-100 p-5">
          <div className="text-xs text-charcoal-500 uppercase tracking-wider mb-2">Expected</div>
          <div className="text-2xl font-heading text-charcoal-800">{inr(summary.totalExpected)}</div>
        </div>
        <div className="bg-green-50 rounded-xl border border-green-100 p-5">
          <div className="text-xs text-green-700 uppercase tracking-wider mb-2 flex items-center gap-1">
            <TrendingUp size={12} /> Received
          </div>
          <div className="text-2xl font-heading text-green-800" data-testid="fee-received-total">{inr(summary.totalReceived)}</div>
        </div>
        <div className="bg-red-50 rounded-xl border border-red-100 p-5">
          <div className="text-xs text-red-700 uppercase tracking-wider mb-2 flex items-center gap-1">
            <AlertCircle size={12} /> Balance Due
          </div>
          <div className="text-2xl font-heading text-red-800" data-testid="fee-due-total">{inr(summary.totalDue)}</div>
        </div>
      </div>

      {/* Transaction table */}
      <div className="bg-white rounded-xl border border-charcoal-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-cream border-b border-charcoal-100">
            <tr>
              <th className="text-left px-4 py-3 font-heading text-charcoal-700">Participant</th>
              <th className="text-left px-4 py-3 font-heading text-charcoal-700">Item</th>
              <th className="text-right px-4 py-3 font-heading text-charcoal-700">Expected</th>
              <th className="text-right px-4 py-3 font-heading text-charcoal-700">Received</th>
              <th className="text-right px-4 py-3 font-heading text-charcoal-700">Due</th>
              <th className="text-left px-4 py-3 font-heading text-charcoal-700">Status</th>
              <th className="text-left px-4 py-3 font-heading text-charcoal-700">Date</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8 text-charcoal-500">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-charcoal-500">No {tab} registrations yet.</td></tr>
            ) : (
              filtered.map((r) => {
                const expected = r.amount || 0;
                const received = r.paid_amount ?? (r.status === 'completed' ? expected : 0);
                const due = Math.max(expected - received, 0);
                return (
                  <tr key={r.id} className="border-b border-charcoal-50 last:border-0" data-testid={`fee-row-${r.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-charcoal-800">{r.participant_name}</div>
                      <div className="text-xs text-charcoal-500">{r.participant_email}</div>
                    </td>
                    <td className="px-4 py-3 text-charcoal-600">{r.event_title || '—'}</td>
                    <td className="px-4 py-3 text-right text-charcoal-700">{inr(expected)}</td>
                    <td className="px-4 py-3 text-right text-green-700 font-medium">{inr(received)}</td>
                    <td className={`px-4 py-3 text-right font-medium ${due > 0 ? 'text-red-700' : 'text-charcoal-500'}`}>{inr(due)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        due === 0 && expected > 0 ? 'bg-green-100 text-green-800' :
                        received > 0 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-charcoal-100 text-charcoal-600'
                      }`}>
                        {due === 0 && expected > 0 ? 'Paid' : received > 0 ? 'Partial' : r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-charcoal-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
